import React, { useState, useEffect } from "react";
import {
  Typography,
  SvgIcon,
  alpha,
  Tooltip,
  Box,
  useTheme,
} from "@mui/material"; // Added Tooltip, Box, useTheme
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import GlassTileLayout from "../layouts/GlassTileLayout"; // Adjust path as needed

// Marine Wave Icon
const WaveIcon = () => (
  <SvgIcon>
    <path d="M3.25 12c0-2.5 2-4.5 4.5-4.5S12.25 9.5 12.25 12s-2 4.5-4.5 4.5-4.5-2-4.5-4.5zm8.5 0c0-2.5 2-4.5 4.5-4.5s4.25 2 4.25 4.5-1.75 4.5-4.25 4.5-4.5-2-4.5-4.5z" />
  </SvgIcon>
);

const LastVizRatingTile = ({
  latestVizComment,
  loading,
  error,
  divesiteId,
}) => {
  const [userLastViz, setUserLastViz] = useState(null);
  const [userLastVizDate, setUserLastVizDate] = useState(null);
  const { userObjectId, currentUser } = useAuth();
  const [loadingUserViz, setLoadingUserViz] = useState(false);
  const [errorUserViz, setErrorUserViz] = useState(null);
  const theme = useTheme(); // Use theme for potential styling in tooltip

  useEffect(() => {
    const fetchUserLastViz = async () => {
      // Only fetch if userObjectId is available and not already loading
      if (userObjectId && divesiteId && currentUser && !loadingUserViz) {
        setLoadingUserViz(true);
        setErrorUserViz(null);
        try {
          const token = await currentUser.getIdToken();
          const response = await axios.get(
            `/api/dives/user/${userObjectId}/divesite/${divesiteId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const dives = response.data;
          // Filter for dives at this specific site that have a visibility rating
          const userDivesAtSite = dives.filter(
            (dive) =>
              (dive.divesite === divesiteId ||
                dive.divesite === divesiteId.toString()) &&
              dive.visibilityRating !== null &&
              dive.visibilityRating !== undefined
          );

          if (userDivesAtSite.length > 0) {
            // Sort by diveDate descending to get the latest
            userDivesAtSite.sort(
              (a, b) => new Date(b.diveDate) - new Date(a.diveDate)
            );
            setUserLastViz(userDivesAtSite[0]?.visibilityRating);
            setUserLastVizDate(userDivesAtSite[0]?.diveDate);
          } else {
            setUserLastViz("N/A");
            setUserLastVizDate(null);
          }
        } catch (error) {
          console.error("Error fetching user dives for viz:", error);
          setErrorUserViz(error);
          setUserLastViz("Error"); // Indicate error in UI
        } finally {
          setLoadingUserViz(false);
        }
      } else if (!userObjectId) {
        // If no userObjectId, ensure state is reset for unauthenticated view
        setUserLastViz("N/A");
        setUserLastVizDate(null);
      }
    };

    fetchUserLastViz();
  }, [userObjectId, divesiteId, currentUser]); // Dependencies for useEffect

  // Function to generate content for the tooltip
  const getTooltipContent = () => {
    const latestOverallViz =
      latestVizComment?.visibilityRating !== null &&
      latestVizComment?.visibilityRating !== undefined
        ? `${latestVizComment.visibilityRating}m`
        : "N/A";
    const latestOverallDate = latestVizComment?.timestamp
      ? new Date(latestVizComment.timestamp).toLocaleDateString()
      : "N/A";

    const userVizValue =
      userLastViz !== null && userLastViz !== undefined && userLastViz !== "N/A"
        ? `${userLastViz}m`
        : "N/A";
    const userVizDateFormatted = userLastVizDate
      ? new Date(userLastVizDate).toLocaleDateString()
      : "N/A";

    return (
      <React.Fragment>
        <Typography
          color="inherit"
          variant="subtitle2"
          sx={{ fontWeight: 600 }}
        >
          Visibility
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          This tile shows the latest reported underwater visibility at the dive
          site.  Each star is approximately 3 meters (10 feet) of visibility.
        </Typography>

        {/* Display user's last visibility if available */}
        {userObjectId && ( // Only show this section if a user is logged in
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Your last dive: {userVizValue}{" "}
            {userVizDateFormatted !== "N/A" && `on ${userVizDateFormatted}`}
            {loadingUserViz && " (loading...)"}
            {errorUserViz && " (error)"}
          </Typography>
        )}

        {/* Display latest overall visibility */}
        <Typography
          variant="caption"
          display="block"
          sx={{ mt: 0.5, fontStyle: "italic" }}
        >
          Latest overall: {latestOverallViz}{" "}
          {latestOverallDate !== "N/A" && `on ${latestOverallDate}`}
        </Typography>
      </React.Fragment>
    );
  };

  return (
    <Tooltip
      title={getTooltipContent()}
      placement="top"
      arrow
      enterDelay={0} // Instant appearance on hover
      leaveDelay={100}
      enterTouchDelay={0} // Instant appearance on tap
    >
      {/* Box wrapper ensures the Tooltip has a direct, interactive DOM element */}
      <Box
        tabIndex={0}
        role="button"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          cursor: "pointer",
        }}
      >
        <GlassTileLayout
          icon={WaveIcon} // Pass the icon component directly
          label="Visibility"
          value={
            loading
              ? "..."
              : error
              ? "Error"
              : latestVizComment?.visibilityRating !== null &&
                latestVizComment?.visibilityRating !== undefined
              ? `${latestVizComment.visibilityRating}m`
              : "N/A"
          }
        >
          {!loading && !error && latestVizComment?.timestamp && (
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.text.secondary, 0.8), // Use theme.palette.text.secondary for better contrast
                fontSize: "0.65rem",
                fontStyle: "italic",
              }}
            >
              {new Date(latestVizComment.timestamp).toLocaleDateString()}
            </Typography>
          )}
        </GlassTileLayout>
      </Box>
    </Tooltip>
  );
};

export default LastVizRatingTile;
